import { HeistsPage } from './pages/Heists.tsx';
import { RacketPage } from './pages/Racket.tsx';
import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { api } from './api.ts';
import { loadAssetManifest } from './art/assets.ts';
import { GameProvider, useGame, duration } from './state.tsx';
import { Header, StatBar, Nav, FlashBox, useRoute, Loading } from './components/ui.tsx';
import { AuthPage } from './pages/Auth.tsx';
import { HomePage } from './pages/Home.tsx';
import { JobsPage } from './pages/Jobs.tsx';
import { FightPage } from './pages/Fight.tsx';
import { TerritoryPage } from './pages/Territory.tsx';
import { PokerPage } from './pages/Poker.tsx';
import { RacesPage } from './pages/Races.tsx';
import { InventoryPage } from './pages/Inventory.tsx';
import { MafiaPage } from './pages/Mafia.tsx';
import { ProfilePage } from './pages/Profile.tsx';
import { BankPage, HospitalPage, GodfatherPage, LeaderboardPage } from './pages/Services.tsx';
import { AdminPage } from './pages/Admin.tsx';
import { CasinoPage } from './pages/Casino.tsx';
import { AchievementsPage } from './pages/Achievements.tsx';
import { PrestigePage } from './pages/Prestige.tsx';
import { CustomizePage } from './pages/Customize.tsx';
import { ChatPage, CHAT_SEEN_KEY } from './pages/Chat.tsx';
import { OptionsPage, SpeakerButton } from './pages/Options.tsx';
import { FxLayer, installPointerTracking } from './fx/fx.tsx';
import { useAlerts } from './fx/alerts.ts';
import { StreakPopup } from './components/daily.tsx';
import { SideBetsPage } from './pages/SideBets.tsx';
import { TitlePopup, AuctionBanner } from './components/v08.tsx';
import { installAudioUnlock, play } from './fx/sound.ts';
import { installMusic } from './fx/music.ts';
import { applyMotionClass, getSettings } from './fx/settings.ts';

// Sound, music and effects start working after the first click (browsers require that).
applyMotionClass();
installAudioUnlock();
installMusic();
installPointerTracking();
document.addEventListener('click', (e) => {
  if (!getSettings().clicks) return;
  const el = (e.target as Element | null)?.closest?.('.btn, .nav a, .subnav a, .fb-bar a, .class-pick label, .chip');
  if (!el || el.classList.contains('speaker')) return;
  play(el.closest('.nav, .subnav, .fb-bar') ? 'tab' : 'click');
}, { capture: true });

function chatUnread(latest: number): number {
  try {
    const seen = Number(localStorage.getItem(CHAT_SEEN_KEY) ?? 0);
    return Math.max(0, latest - seen);
  } catch {
    return 0;
  }
}

/** Banner for world events an admin started (Double XP, Police Crackdown, Bounty Frenzy). */
function EventBanner() {
  const { me } = useGame();
  const events = me?.worldEvents ?? [];
  if (!events.length) return null;
  return (
    <>
      {events.map((e: any) => (
        <div key={e.kind} className={`event-banner ev-${e.kind}`}>
          <b>{e.name}!</b> {e.description}{e.message && <i> “{e.message}”</i>}
          <span className="small"> Ends in {duration(Math.max(0, e.endsAt - Date.now()))}.</span>
        </div>
      ))}
    </>
  );
}

/** v0.7: a red ribbon while a siege involves you, with a live countdown. */
function SiegeBanner() {
  const { me, content } = useGame();
  const sieges = me?.sieges ?? [];
  const [, tick] = useState(0);
  useEffect(() => {
    if (!sieges.length) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [sieges.length]);
  if (!sieges.length) return null;
  const name = (id: string) => content.boroughs?.find((b: any) => b.id === id)?.name ?? id;
  return (
    <>
      {sieges.map((s: any) => {
        const left = Math.max(0, s.endsAt - Date.now());
        const mm = `${Math.floor(left / 60000)}:${String(Math.floor(left / 1000) % 60).padStart(2, '0')}`;
        return (
          <div key={s.id} className={`siege-banner ${s.role}`}>
            <span className="siege-tag">Siege</span>
            <span className="grow">
              {s.role === 'attacker' ? <>Your crews are besieging <b>{name(s.boroughId)}</b>{s.defenderName && <> (held by {s.defenderName})</>}.</>
                : s.role === 'defender' ? <><b>{s.attackerName}</b> is besieging your turf in <b>{name(s.boroughId)}</b>. Reinforce it or call in friends.</>
                  : <>{s.defenderName} asked you to help hold <b>{name(s.boroughId)}</b> against {s.attackerName}.</>}
            </span>
            <span className="siege-clock" title="Time until the battle">{mm}</span>
            <a className="btn small red" href={`#/territory/siege-${s.boroughId}`}>{s.role === 'attacker' ? 'Watch' : 'Defend'}</a>
          </div>
        );
      })}
    </>
  );
}

/** "Achievement unlocked" banner that explains what each new achievement was for. */
function AchievementPopup() {
  const { me, byId, act } = useGame();
  const list = me?.newAchievements ?? [];
  if (!list.length) return null;
  return (
    <div className="ach-popup" key={list.length}>
      <div className="ach-badge medal-drop">★</div>
      <div className="grow">
        {list.map((a: any) => {
          const def = byId.achievements.get(a.id);
          return (
            <div key={a.id} style={{ marginBottom: 2 }}>
              <b className="gold">{a.hidden ? 'Secret achievement' : 'Achievement'} unlocked: {a.name ?? def?.name}</b>{a.tier && <span className={`t-${a.tier}`}> ({a.tier})</span>} (+{a.favor ?? def?.favor} favor points)
              <div className="small">Why: {a.description ?? def?.description}</div>
            </div>
          );
        })}
        <a className="small" href="#/achievements">See all achievements</a>
      </div>
      <button className="btn small" onClick={() => act('/api/achievements/seen')}>OK</button>
    </div>
  );
}

/** Patch notes shown once after the game is updated. */
function WhatsNew() {
  const { whatsNew, dismissWhatsNew } = useGame();
  if (!whatsNew.length) return null;
  return (
    <div className="modal-back" onClick={dismissWhatsNew}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">What’s new</div>
        <div className="modal-body">
          {whatsNew.map((c: any) => (
            <div key={c.version} style={{ marginBottom: 10 }}>
              <h2 style={{ marginTop: 0 }}>{c.title} <span className="dim small">v{c.version} · {c.date}</span></h2>
              <ul>{c.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}</ul>
            </div>
          ))}
          <div className="right"><button className="btn big" onClick={dismissWhatsNew}>Got it</button></div>
        </div>
      </div>
    </div>
  );
}

function Shell() {
  const route = useRoute();
  const { setFlash, content, me, whatsNew } = useGame();
  const [page, arg] = route;
  // Clear the message box when moving between pages.
  useEffect(() => setFlash(null), [page, arg]);
  // Tab title, tab icon dot, sounds and desktop pop-ups for things worth coming back for.
  useAlerts(me, content.gameName);
  let body;
  // Pages assume your character is loaded; show "Loading" for the moment after a page refresh.
  if (!me) body = <Loading />;
  else switch (page) {
    case 'jobs': body = <JobsPage tierId={arg} />; break;
    case 'fight': body = <FightPage tab={arg} />; break;
    case 'customize': body = <CustomizePage />; break;
    case 'chat': body = <ChatPage />; break;
    case 'heists': body = <HeistsPage />; break;
    case 'racket': body = <RacketPage />; break;
    case 'properties': case 'territory': body = <TerritoryPage tab={arg} />; break;
    case 'poker': body = <PokerPage table={arg} />; break;
    case 'races': body = <RacesPage />; break;
    case 'sidebets': body = <SideBetsPage arg={arg} />; break;
    case 'inventory': body = <InventoryPage tab={arg} />; break;
    case 'mafia': body = <MafiaPage />; break;
    case 'profile': body = <ProfilePage id={Number(arg)} />; break;
    case 'bank': body = <BankPage />; break;
    case 'hospital': body = <HospitalPage />; break;
    case 'godfather': body = <GodfatherPage />; break;
    case 'leaderboard': body = <LeaderboardPage board={arg} />; break;
    case 'admin': body = <AdminPage tab={arg} />; break;
    case 'casino': body = <CasinoPage game={arg} />; break;
    case 'achievements': body = <AchievementsPage tab={arg} />; break;
    case 'options': body = <OptionsPage />; break;
    case 'prestige': body = <PrestigePage />; break;
    default: body = <HomePage />;
  }
  return (
    <>
      <div className="fb-bar">
        <div className="fb-bar-inner">
          <span className="brand">{content.gameName}</span>
          <span className="spacer" />
          <a href="#/home">Home</a>
          <a href={me ? `#/profile/${me.id}` : '#/home'}>Profile</a>
          <a href="#/racket" className="racket-link">The Daily Racket</a>
          <a href="#/chat">Chat{page !== 'chat' && me && chatUnread(me.latestChatId) > 0 && <span className="badge">{Math.min(99, chatUnread(me.latestChatId))}</span>}</a>
          <a href="#/achievements">Achievements</a>
          <a href="#/leaderboard">Leaderboards</a>
          <a href="#/options" title="Sound, music and animations">⚙ Options</a>
          <SpeakerButton />
        </div>
      </div>
      <div className="game">
        <Header />
        <StatBar />
        <Nav current={page === 'poker' || page === 'races' || page === 'sidebets' ? 'casino' : page === 'properties' ? 'territory' : page} />
        <div className="content">
          <EventBanner />
          <SiegeBanner />
          <AuctionBanner />
          <AchievementPopup />
          <TitlePopup />
          <FlashBox />
          {body}
        </div>
        <div className="footer">{content.gameName} v{content.version} · a fan-made tribute to the classic browser mafia games of 2008–2011. All art is original.</div>
      </div>
      <WhatsNew />
      {me?.streak && whatsNew.length === 0 && <StreakPopup />}
      <FxLayer />
    </>
  );
}

function App() {
  const [content, setContent] = useState<any>(null);
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const route = useRoute();

  useEffect(() => {
    Promise.all([api.get('/api/content'), api.get('/api/auth/session'), loadAssetManifest()]).then(([c, s]) => {
      setContent(c);
      setLoggedIn(s.loggedIn);
    });
  }, []);
  const onLoggedOut = useCallback(() => setLoggedIn(false), []);

  if (!content || loggedIn === null) return <Loading />;
  if (route[0] === 'reset' && route[1]) return <AuthPage content={content} resetToken={route[1]} onLoggedIn={() => setLoggedIn(true)} />;
  if (!loggedIn) return <AuthPage content={content} onLoggedIn={() => { window.location.hash = '#/home'; setLoggedIn(true); }} />;
  return (
    <GameProvider content={content} onLoggedOut={onLoggedOut}>
      <Shell />
    </GameProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
